import { useCallback, useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { Content, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { Chip, makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  ready: { backgroundColor: '#228722', color: '#fff', height: 22, fontSize: '0.75rem' },
  notReady: { backgroundColor: '#cd3c14', color: '#fff', height: 22, fontSize: '0.75rem' },
  unknown: { backgroundColor: theme.palette.grey[500], color: '#fff', height: 22, fontSize: '0.75rem' },
}));

interface K8sNodeCondition {
  type: string;
  status: string;
  reason?: string;
  message?: string;
}

interface K8sNodeAddress {
  type: string;
  address: string;
}

interface K8sNode {
  metadata: {
    name: string;
    creationTimestamp?: string;
    labels?: Record<string, string>;
  };
  status?: {
    nodeInfo?: {
      kubeletVersion?: string;
      osImage?: string;
    };
    capacity?: {
      cpu?: string;
      memory?: string;
    };
    addresses?: K8sNodeAddress[];
    conditions?: K8sNodeCondition[];
  };
}

interface K8sNodeList {
  items: K8sNode[];
}

type NodeRow = {
  name: string;
  roles: string;
  status: string;
  kubeletVersion: string;
  os: string;
  cpu: string;
  memory: string;
  internalIp: string;
  age: string;
  failureReason: string;
};

function formatAge(timestamp?: string): string {
  if (!timestamp) return '—';
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function parseMemory(raw: string): string {
  const ki = parseInt(raw.replace('Ki', ''), 10);
  if (isNaN(ki)) return raw;
  return `${(ki / 1024 / 1024).toFixed(1)} GiB`;
}

function parseCpu(raw: string): string {
  if (raw.endsWith('m')) return `${parseInt(raw, 10) / 1000} cores`;
  return `${raw} cores`;
}

function nodeRoles(labels: Record<string, string>): string {
  const isControlPlane = Object.keys(labels).some(k =>
    k === 'node-role.kubernetes.io/control-plane' ||
    k === 'node-role.kubernetes.io/master',
  );
  return isControlPlane ? 'control-plane' : 'compute';
}

function nodeStatus(conditions: K8sNodeCondition[]): string {
  const ready = conditions.find(c => c.type === 'Ready');
  if (!ready) return 'Unknown';
  return ready.status === 'True' ? 'Ready' : 'NotReady';
}

function nodeFailureReason(conditions: K8sNodeCondition[]): string {
  const ready = conditions.find(c => c.type === 'Ready');
  if (!ready || ready.status === 'True') return '';
  return ready.reason ?? ready.message ?? '';
}

function parseNode(n: K8sNode): NodeRow {
  const conditions = n.status?.conditions ?? [];
  return {
    name: n.metadata.name,
    roles: nodeRoles(n.metadata.labels ?? {}),
    status: nodeStatus(conditions),
    kubeletVersion: n.status?.nodeInfo?.kubeletVersion ?? '—',
    os: n.status?.nodeInfo?.osImage ?? '—',
    cpu: parseCpu(n.status?.capacity?.cpu ?? '—'),
    memory: parseMemory(n.status?.capacity?.memory ?? ''),
    internalIp:
      (n.status?.addresses ?? []).find(a => a.type === 'InternalIP')?.address ?? '—',
    age: formatAge(n.metadata.creationTimestamp),
    failureReason: nodeFailureReason(conditions),
  };
}

export const ClusterNodesTab = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const kubernetesApi = useApi(kubernetesApiRef);

  const clusterName = entity.metadata.annotations?.['backstage.io/kubernetes-id'] ?? entity.metadata.name;

  const [rows, setRows] = useState<NodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await kubernetesApi.proxy({
        clusterName,
        path: '/api/v1/nodes',
      });
      if (!resp.ok) {
        throw new Error(`Kubernetes API returned ${resp.status}`);
      }
      const body = (await resp.json()) as K8sNodeList;
      setRows((body.items ?? []).map(parseNode));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch nodes');
    } finally {
      setLoading(false);
    }
  }, [kubernetesApi, clusterName]);

  useEffect(() => {
    load();
  }, [load]);

  const statusChipClass = (status: string) => {
    if (status === 'Ready') return classes.ready;
    if (status === 'NotReady') return classes.notReady;
    return classes.unknown;
  };

  const typedColumns: TableColumn<NodeRow>[] = [
    { title: 'Name', field: 'name', render: r => <span style={{ fontFamily: 'monospace' }}>{r.name}</span> },
    {
      title: 'Status',
      field: 'status',
      render: r => <Chip label={r.status} className={statusChipClass(r.status)} />,
    },
    { title: 'Roles', field: 'roles' },
    { title: 'Version', field: 'kubeletVersion' },
    { title: 'CPU', field: 'cpu' },
    { title: 'Memory', field: 'memory' },
    { title: 'Internal IP', field: 'internalIp', render: r => <span style={{ fontFamily: 'monospace' }}>{r.internalIp}</span> },
    { title: 'OS', field: 'os' },
    { title: 'Age', field: 'age' },
    {
      title: 'Failure Reason',
      field: 'failureReason',
      render: r => r.failureReason ? <span style={{ color: '#cd3c14' }}>{r.failureReason}</span> : <span style={{ color: '#aaa' }}>—</span>,
    },
  ];

  return (
    <Content>
      {error && (
        <WarningPanel title="Failed to fetch nodes" message={error} />
      )}
      <Table<NodeRow>
        title={`Nodes (${rows.length})`}
        isLoading={loading}
        data={rows}
        columns={typedColumns}
        options={{ paging: false, search: false }}
      />
    </Content>
  );
};
